window.tooltipInterop = {
  // Bootstrap's Tooltip.dispose() copies data-bs-original-title back into a real
  // `title` attribute. Left alone that resurrects the browser's native tooltip on
  // any element that outlives its Blazor component, so strip it after every dispose.
  stripTitle: (element) => {
    try {
      if (element && element.removeAttribute) {
        element.removeAttribute('title');
      }
    } catch {
      // ignore
    }
  },

  ensure: (element, options) => {
    if (!element) {
      console.warn('SafeTooltip: Element is null or undefined');
      return false;
    }

    if (!window.bootstrap || !window.bootstrap.Tooltip) {
      console.warn('SafeTooltip: Bootstrap is not loaded yet');
      return false;
    }

    // Don't create tooltips for detached nodes (common during Blazor re-render frames)
    try {
      if (!document.contains(element)) {
        return false;
      }
    } catch {
      // ignore
    }

    try {
      let instance = window.bootstrap.Tooltip.getInstance(element);
      if (instance) {
        // Keep existing instance in sync with latest options
        window.tooltipInterop.update(element, options);
        return true;
      }

      const merged = {
        trigger: 'hover focus',
        title: '',
        html: false,
        sanitize: true,
        // In SPAs, tooltips can get clipped by overflow/positioning; body container is most reliable.
        container: document.body,
        ...options
      };

      // Remove any title attribute to prevent default browser tooltip
      if (element.hasAttribute('title')) {
        element.removeAttribute('title');
      }

      instance = new window.bootstrap.Tooltip(element, merged);

      // Defensive: some failures happen only after first trigger; precompute placement once.
      try {
        instance.update();
      } catch {
        // ignore
      }

      return true;
    } catch (error) {
      console.error('SafeTooltip: Error creating tooltip', error);
      return false;
    }
  },

  update: (element, options) => {
    if (!element || !window.bootstrap || !window.bootstrap.Tooltip) {
      return;
    }

    let tt = null;
    try {
      tt = window.bootstrap.Tooltip.getInstance(element);
    } catch {
      tt = null;
    }

    // If no instance yet, attempt creation.
    if (!tt) {
      window.tooltipInterop.ensure(element, options);
      return;
    }

    // Empty titles should effectively disable the tooltip.
    const hasTitle = options && Object.prototype.hasOwnProperty.call(options, 'title');
    const titleValue = hasTitle ? (options.title ?? '') : null;

    try {
      if (hasTitle) {
        // Bootstrap's _getTitle()/_isWithContent() read _config.title first and
        // fall back to the data-bs-original-title attribute. A tooltip created
        // with an empty title (e.g. content bound to async data that wasn't
        // loaded on first render) keeps _config.title empty, so setContent()
        // alone leaves _isWithContent() false and show() bails out — the tooltip
        // never appears. Always set the attribute so Bootstrap sees the content,
        // and additionally call setContent() to refresh any visible tip (v5.2+).
        element.setAttribute('data-bs-original-title', titleValue);
        if (typeof tt.setContent === 'function') {
          tt.setContent({ '.tooltip-inner': titleValue });
        }
      }

      // Keep popper position up to date.
      tt.update();
    } catch (e) {
      // If update fails (common during rapid Blazor re-render), recreate the instance.
      try {
        tt.dispose();
      } catch {
        // ignore
      }
      window.tooltipInterop.stripTitle(element);
      try {
        window.tooltipInterop.ensure(element, options);
      } catch {
        // ignore
      }
    }
  },

  hide: (element) => {
    if (!element || !window.bootstrap || !window.bootstrap.Tooltip) {
      return;
    }

    const tt = window.bootstrap.Tooltip.getInstance(element);
    if (!tt) {
      return;
    }

    try {
      tt.hide();
    } catch {
      // ignore
    }
  },

  hideThenDispose: (element) => {
    if (!element || !window.bootstrap || !window.bootstrap.Tooltip) {
      return;
    }

    const tt = window.bootstrap.Tooltip.getInstance(element);
    if (!tt) {
      return;
    }

    const disposeSafe = () => {
      try {
        tt.dispose();
      } catch {
        // ignore
      }
      window.tooltipInterop.stripTitle(element);
    };

    // If not shown, dispose immediately.
    const tipEl = tt.tip;
    const isShown = !!(tipEl && tipEl.classList && tipEl.classList.contains('show'));

    if (!isShown) {
      disposeSafe();
      return;
    }

    const onHidden = () => {
      element.removeEventListener('hidden.bs.tooltip', onHidden);
      disposeSafe();
    };

    element.addEventListener('hidden.bs.tooltip', onHidden, { once: true });

    try {
      tt.hide();
    } catch {
      element.removeEventListener('hidden.bs.tooltip', onHidden);
      disposeSafe();
    }
  },

  // Useful when Blazor updates chunks of UI via navigation/conditional rendering.
  // Recreate tooltips for any elements that declare they want tooltips.
  reinitializeAll: (rootSelector) => {
    if (!window.bootstrap || !window.bootstrap.Tooltip) {
      return 0;
    }

    const root = rootSelector ? document.querySelector(rootSelector) : document;
    if (!root) {
      return 0;
    }

    let count = 0;
    const nodes = root.querySelectorAll('[data-bs-toggle="tooltip"]');
    nodes.forEach((el) => {
      try {
        if (!document.contains(el)) return;
        const existing = window.bootstrap.Tooltip.getInstance(el);
        if (existing) {
          existing.update();
        } else {
          // Use data attributes already on the element
          window.tooltipInterop.ensure(el, {});
        }
        count++;
      } catch {
        // ignore per element
      }
    });

    return count;
  }
};
